import { BusinessPurpose } from '@ebalance/proto/bfftypes/v1/business_purpose_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { Control, FieldErrors, FieldValues } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Checkbox,
  FieldRequiredIndicator,
  FieldRoot,
  HStack,
  Input,
  Stack,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import {
  type BusinessMasterFormValues,
  isRegistrationNumberRequired,
} from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/schema'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'

type Props = {
  control: Control<BusinessMasterFormValues>
  errors: FieldErrors<BusinessMasterFormValues>
  /**
   * true の場合 businessCode を readonly にする (edit 時)。
   * AC-006 準拠: business_code は登録後に変更不可。
   */
  isEdit?: boolean
}

// spec business.md: ライセンス区分は 3 択 (小売 / 発電 / DR)。
// TRANSMISSION (送配電) は Phase1 スコープ外、SELF_WHEELING (自己託送) は
// ライセンス区分でなく取得目的 (Purpose) で表現する (issue #3072 論点7)。
// backend entity の validateLicenseTypes が TRANSMISSION / SELF_WHEELING を reject。
const LICENSE_TYPE_OPTIONS: Array<{ value: LicenseType; labelKey: string }> = [
  {
    value: LicenseType.GENERATION,
    labelKey: 'page:adminOcctoMaster.businessMaster.licenseType.generation',
  },
  {
    value: LicenseType.RETAIL,
    labelKey: 'page:adminOcctoMaster.businessMaster.licenseType.retail',
  },
  {
    value: LicenseType.DR,
    labelKey: 'page:adminOcctoMaster.businessMaster.licenseType.dr',
  },
]

const PURPOSE_OPTIONS: Array<{
  value: BusinessPurpose
  labelKey: string
}> = [
  {
    value: BusinessPurpose.NORMAL,
    labelKey: 'page:adminOcctoMaster.businessMaster.purpose.normal',
  },
  {
    value: BusinessPurpose.BATTERY,
    labelKey: 'page:adminOcctoMaster.businessMaster.purpose.battery',
  },
  {
    value: BusinessPurpose.PUMPED_HYDRO,
    labelKey: 'page:adminOcctoMaster.businessMaster.purpose.pumpedHydro',
  },
  {
    value: BusinessPurpose.SELF_WHEELING,
    labelKey: 'page:adminOcctoMaster.businessMaster.purpose.selfWheeling',
  },
]

/**
 * BusinessMaster の Create / Edit 共通 form fields コンポーネント。
 *
 * isEdit={true} のとき businessCode を readonly にする (AC-006)。
 * Controller pattern で React Hook Form の control に接続する。
 */
export const BusinessMasterFormFields = ({
  control,
  errors,
  isEdit = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const licenseTypes = useWatch({ control, name: 'licenseTypes' })
  const purpose = useWatch({ control, name: 'purpose' })
  const registrationNumberRequired = isRegistrationNumberRequired(
    licenseTypes ?? [],
    purpose,
  )

  return (
    // design (`design/admin/occto.html` #occto-edit-modal body) は各フィールド
    // 行に `margin-bottom: var(--space-3)` (=12px) を付与しており、 flex gap
    // でこれを再現する。 gap={4} (16px) では 4px 広い。
    <Stack gap={3}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix="businessMaster"
        />
      )}

      {/* businessCode */}
      <Controller
        control={control}
        name="businessCode"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={errors.businessCode !== undefined}
            required
          >
            <FieldLabel htmlFor="businessMaster-businessCode">
              {t('page:adminOcctoMaster.businessMaster.form.businessCode')}
              <FieldRequiredIndicator />
            </FieldLabel>
            <ImmutableInput
              {...field}
              width="full"
              id="businessMaster-businessCode"
              maxLength={4}
              placeholder={t(
                'page:adminOcctoMaster.businessMaster.form.businessCodePlaceholder',
              )}
              readOnly={isEdit}
            />
            {errors.businessCode !== undefined && (
              <FieldErrorText>{errors.businessCode.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      {/* businessName */}
      <Controller
        control={control}
        name="businessName"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={errors.businessName !== undefined}
            required
          >
            <FieldLabel htmlFor="businessMaster-businessName">
              {t('page:adminOcctoMaster.businessMaster.form.businessName')}
              <FieldRequiredIndicator />
            </FieldLabel>
            <Input
              {...field}
              width="full"
              id="businessMaster-businessName"
              maxLength={50}
              placeholder={t(
                'page:adminOcctoMaster.businessMaster.form.businessNamePlaceholder',
              )}
            />
            {errors.businessName !== undefined && (
              <FieldErrorText>{errors.businessName.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      <Controller
        control={control}
        name="registrationNumber"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={errors.registrationNumber !== undefined}
            required={registrationNumberRequired}
          >
            <FieldLabel htmlFor="businessMaster-registrationNumber">
              {t(
                'page:adminOcctoMaster.businessMaster.form.registrationNumber',
              )}
              {registrationNumberRequired && <FieldRequiredIndicator />}
            </FieldLabel>
            <Input
              {...field}
              width="full"
              id="businessMaster-registrationNumber"
              maxLength={20}
              placeholder={t(
                'page:adminOcctoMaster.businessMaster.form.registrationNumberPlaceholder',
              )}
              required={registrationNumberRequired}
            />
            {errors.registrationNumber !== undefined && (
              <FieldErrorText>
                {errors.registrationNumber.message}
              </FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      {/* licenseTypes (複数選択 checkbox) */}
      <Controller
        control={control}
        name="licenseTypes"
        render={({ field: { value, onChange } }) => {
          const invalid = errors.licenseTypes !== undefined
          return (
            <Stack gap={1}>
              <FieldRoot invalid={invalid} required>
                <FieldLabel>
                  {t('page:adminOcctoMaster.businessMaster.form.licenseTypes')}
                  <FieldRequiredIndicator />
                </FieldLabel>
              </FieldRoot>
              <HStack flexWrap="wrap" gap={4}>
                {LICENSE_TYPE_OPTIONS.map((opt) => {
                  const licenseValue: number = opt.value
                  return (
                    <Checkbox
                      key={opt.value}
                      checked={value.includes(licenseValue)}
                      onCheckedChange={(details: {
                        checked: boolean | 'indeterminate'
                      }) => {
                        if (details.checked === true) {
                          onChange([...value, licenseValue])
                        } else {
                          onChange(value.filter((v) => v !== licenseValue))
                        }
                      }}
                    >
                      {t(opt.labelKey)}
                    </Checkbox>
                  )
                })}
              </HStack>
              {invalid && (
                <FieldRoot invalid>
                  <FieldErrorText>
                    {errors.licenseTypes?.message ??
                      errors.licenseTypes?.root?.message}
                  </FieldErrorText>
                </FieldRoot>
              )}
            </Stack>
          )
        }}
      />

      {/* purpose (取得目的 — 単一選択)。 単一 select の正は DS `Select`
          (`~/components/common/FormSelect` の JSDoc 参照)。 */}
      <FieldRoot
        alignItems="stretch"
        invalid={errors.purpose !== undefined}
        required
      >
        <FieldLabel>
          {t('page:adminOcctoMaster.businessMaster.form.purpose')}
          <FieldRequiredIndicator />
        </FieldLabel>
        <FormSelect
          aria-label={t('page:adminOcctoMaster.businessMaster.form.purpose')}
          control={control}
          name="purpose"
          numeric
          options={PURPOSE_OPTIONS.map((opt) => ({
            label: t(opt.labelKey),
            value: String(opt.value),
          }))}
        />
        {errors.purpose !== undefined && (
          <FieldErrorText>{errors.purpose.message}</FieldErrorText>
        )}
      </FieldRoot>

      {/* validFrom + validTo — 共通 TopLevelValidPeriodFields */}
      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        idPrefix="businessMaster"
        validFromError={
          'validFrom' in errors ? errors.validFrom?.message : undefined
        }
        validToError={'validTo' in errors ? errors.validTo?.message : undefined}
      />
    </Stack>
  )
}
